import React, {useEffect, useState} from 'react';

import {createNativeStackNavigator} from '@react-navigation/native-stack';
import Geolocation from 'react-native-geolocation-service';
import {StackParamList} from '../stacks';
import {
  DebtorsScreen,
  SalesmanPortfolioDashboardScreen,
  SalesmanReceivableList,
} from '../../screens/internals/salesman';

import {DashboardScreen as SalesmanDashboardScreen} from '../../screens/internals/salesman/dashboard/DashboardScreen.tsx';

import {NotificationsScreen, ProfileScreen} from '../../screens/internals';

import {
  SalesmanClientList,
  SalesmanClientDashboard,
} from '../../screens/internals/salesman/portfolio/clients';
import {ProofPaymentScreen} from '../../screens/internals/commons/proof-payment';
import {
  ProofSuccessScreen,
  RemoveSuccessScreen,
  SalesmanNewClientRequestSuccess,
  SendSuccessScreen,
} from '../../screens/internals/commons/success';
import {
  BalanceDetail,
  BalanceList,
} from '../../screens/internals/commons/balance';
import {
  HistoryDetail,
  HistoryList,
} from '../../screens/internals/commons/history';
import {AddClientStep1Screen} from '../../screens/internals/salesman/portfolio/clients/add';
import {AddClientStep2Screen} from '../../screens/internals/salesman/portfolio/clients/add';
import {AddClientStep3Screen} from '../../screens/internals/salesman/portfolio/clients/add';
import {HelpDocumentsScreen} from '../../screens/internals/salesman/portfolio/clients/add';
import {
  SalesmanOrderBeginScreen,
  SalesmanOrderEndScreen,
  SalesmanOrderSuccessScreen,
  SalesmanOrderAddScreen,
  SalesmanOrderShoppingCartScreen,
  SalesmanProductSearch,
} from '../../screens/internals/salesman/portfolio/orders/create';
import {useContextState as useUserContext} from '../../context/user';
import {SalesmanProvider} from '../../context/salesman';
import {FilterPortfolioScreen} from '../../screens/internals/commons/portfolio-filter';
import OrderListScreen from '../../screens/internals/salesman/orders/OrderList';
import OrderListFilterScreen from '../../screens/internals/salesman/orders/OrderListFilter';
import OrderSummaryScreen from '../../screens/internals/salesman/orders/OrderSummary';
import OrderDetailScreen from '../../screens/internals/salesman/orders/OrderDetail';
import SalesmanScheduleListScreen from '../../screens/internals/salesman/portfolio/schedule/ScheduleList';
import SalesmanScheduleDetailScreen from '../../screens/internals/salesman/portfolio/schedule/ScheduleDetail';
import SalesmanScheduleSuccessScreen from '../../screens/internals/salesman/portfolio/schedule/ScheduleSuccess';
import ScheduleCodeScanner from '../../screens/internals/salesman/portfolio/schedule/ScheduleCodeScanner';
import {
  androidWatchPosition,
  startWatchPosition,
} from '../../utils/localization/location-tracking';
import {Platform} from 'react-native';
import {startForeground} from '../../utils/localization/foreground-actions';
import InvoiceCandidateList from '../../screens/internals/salesman/portfolio/clients/invoices/invoice-candidate-list';
import InvoiceAdjustableItems from '../../screens/internals/salesman/portfolio/clients/invoices/invoice-adjustable-items';
import InvoiceItemForm from '../../screens/internals/salesman/portfolio/clients/invoices/invoice-item-form';
import InvoiceSummary from '../../screens/internals/salesman/portfolio/clients/invoices/invoice-summary';
import InvoiceSuccessFull from '../../screens/internals/salesman/portfolio/clients/invoices/invoice-successfull';
import WithoutConnectionConfirm from '../../screens/internals/commons/without-connection/confirm-save';
import WithoutConnectionSaveSucess from '../../screens/internals/commons/without-connection/sucess-save';
import WithoutConnectionRequestList from '../../screens/internals/commons/without-connection/request-list/request-list';
import MapModalScreen from '../../components/inputs/maparea/modal/MapModal';
import OrderDashboardScreen from '../../screens/internals/salesman/portfolio/orders/dashboard';
import OrderListCandidateScreen from '../../screens/internals/salesman/portfolio/orders/candidates';

const Stack = createNativeStackNavigator<StackParamList>();

type StackGroupProps = {
  groupOptions: {
    screenOptions: {headerTransparent: boolean; headerTitle: string};
  };
};
const DEFAULT_TIME_LAUNCHER = 90000;

export const NavigatorSalesman = ({groupOptions}: StackGroupProps) => {
  const {uiConfiguration, isLocalizationActive, profile} = useUserContext();
  const {interval} = uiConfiguration?.geoLocation ?? {interval: null};
  const [geoTrackingTaskId, setGeoTrackingTaskId] = useState(
    null as number | null,
  );

  const removeLocalization = () => {
    if (geoTrackingTaskId != null && Platform.OS === 'ios') {
      Geolocation.clearWatch(geoTrackingTaskId);
    } else {
      Geolocation.stopObserving();
    }
  };

  const launchLocalization = () => {
    startWatchPosition(getDefaultTimer(), profile?.referenceId!, id => {
      setGeoTrackingTaskId(id);
    });
  };

  const getDefaultTimer = () => {
    return interval ?? DEFAULT_TIME_LAUNCHER;
  };

  const androidForegroundAction = () => {
    androidWatchPosition(getDefaultTimer(), profile?.referenceId!);
  };

  useEffect(() => {
    if (isLocalizationActive) {
      if (profile?.referenceId && geoTrackingTaskId == null) {
        if (Platform.OS === 'ios') {
          launchLocalization();
        } else {
          startForeground(androidForegroundAction, getDefaultTimer());
        }
      }
    } else if (geoTrackingTaskId !== null) {
      removeLocalization();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalizationActive, geoTrackingTaskId]);

  return (
    <SalesmanProvider>
      <Stack.Navigator
        initialRouteName={'SalesmanDashboard'}
        screenOptions={{headerShown: false}}>
        <Stack.Group {...groupOptions}>
          <Stack.Screen
            name="SalesmanDashboard"
            component={SalesmanDashboardScreen}
          />
          <Stack.Screen name="Profile" component={ProfileScreen} />
          <Stack.Screen name="Notifications" component={NotificationsScreen} />
          <Stack.Screen
            name="SalesmanPortfolioDashboard"
            component={SalesmanPortfolioDashboardScreen}
          />
          <Stack.Screen
            name="PortfolioFilter"
            component={FilterPortfolioScreen}
          />
          <Stack.Screen name="SalesmanDebtors" component={DebtorsScreen} />
          <Stack.Screen
            name="SalesmanReceivables"
            component={SalesmanReceivableList}
          />
          <Stack.Screen
            name="SalesmanClientList"
            component={SalesmanClientList}
          />
          <Stack.Screen
            name="SalesmanClientDashboard"
            component={SalesmanClientDashboard}
          />
          <Stack.Screen name="ProofPayment" component={ProofPaymentScreen} />
          <Stack.Screen name="ProofSuccess" component={ProofSuccessScreen} />
          <Stack.Screen name="BalanceDetail" component={BalanceDetail} />
          <Stack.Screen name="BalanceList" component={BalanceList} />
          <Stack.Screen name="HistoryList" component={HistoryList} />
          <Stack.Screen name="HistoryDetail" component={HistoryDetail} />
          <Stack.Screen name="SendSuccess" component={SendSuccessScreen} />
          <Stack.Screen name="RemoveSuccess" component={RemoveSuccessScreen} />
          <Stack.Screen
            name="AddClientStep1Screen"
            component={AddClientStep1Screen}
          />
          <Stack.Screen
            name="AddClientStep2Screen"
            component={AddClientStep2Screen}
          />
          <Stack.Screen
            name="AddClientStep3Screen"
            component={AddClientStep3Screen}
          />
          <Stack.Screen
            name="SalesmanNewClientRequestSuccess"
            component={SalesmanNewClientRequestSuccess}
          />
          <Stack.Screen
            name="SalesmanCustomerHelpDocuments"
            component={HelpDocumentsScreen}
          />
          <Stack.Screen
            name="SalesmanOrderBegin"
            component={SalesmanOrderBeginScreen}
          />
          <Stack.Screen
            name="SalesmanOrderEnd"
            component={SalesmanOrderEndScreen}
          />
          <Stack.Screen
            name="SalesmanOrderSuccess"
            component={SalesmanOrderSuccessScreen}
          />
          <Stack.Screen
            name="SalesmanOrderAdd"
            component={SalesmanOrderAddScreen}
          />
          <Stack.Screen
            name="SalesmanOrderShoppingCart"
            component={SalesmanOrderShoppingCartScreen}
          />
          <Stack.Screen
            name="SalesmanProductSearch"
            component={SalesmanProductSearch}
          />
          <Stack.Screen name="SalesmanOrderList" component={OrderListScreen} />
          <Stack.Screen
            name="SalesmanOrderListFilter"
            component={OrderListFilterScreen}
          />
          <Stack.Screen
            name="SalesmanOrderSummary"
            component={OrderSummaryScreen}
          />
          <Stack.Screen
            name="SalesmanOrderDetail"
            component={OrderDetailScreen}
          />
          <Stack.Screen
            name="SalesmanScheduleList"
            component={SalesmanScheduleListScreen}
          />
          <Stack.Screen
            name="SalesmanScheduleDetail"
            component={SalesmanScheduleDetailScreen}
          />
          <Stack.Screen
            name="SalesmanScheduleSuccess"
            component={SalesmanScheduleSuccessScreen}
          />
          <Stack.Screen
            name="ScheduleCodeScanner"
            component={ScheduleCodeScanner}
          />
          <Stack.Screen
            name="InvoiceCandidateList"
            component={InvoiceCandidateList}
          />
          <Stack.Screen
            name="InvoiceAdjustableItems"
            component={InvoiceAdjustableItems}
          />
          <Stack.Screen name="InvoiceItemForm" component={InvoiceItemForm} />
          <Stack.Screen name="InvoiceSummary" component={InvoiceSummary} />
          <Stack.Screen
            name="InvoiceSuccessFull"
            component={InvoiceSuccessFull}
          />
          <Stack.Screen
            name="WithoutConnectionConfirm"
            component={WithoutConnectionConfirm}
          />
          <Stack.Screen
            name="WithoutConnectionSaveSucess"
            component={WithoutConnectionSaveSucess}
          />
          <Stack.Screen
            name="WithoutConnectionRequestList"
            component={WithoutConnectionRequestList}
          />
          <Stack.Screen name="MapModalScreen" component={MapModalScreen} />
          <Stack.Screen
            name="OrderDashboardScreen"
            component={OrderDashboardScreen}
          />
          <Stack.Screen
            name="OrderListCandidateScreen"
            component={OrderListCandidateScreen}
          />
        </Stack.Group>
      </Stack.Navigator>
    </SalesmanProvider>
  );
};
